import React, { useCallback, useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import { FormProvider, useForm } from 'react-hook-form'

import { User } from '@/Components/Pages/Accounts/types'

import useCsrfToken from '@/hooks/useCsrfToken'

import Button from '@/Components/Common/Button/Button'
import FormCard from '@/Components/Common/FormCard/FormCard'
import TabPanel from '@/Components/Common/Tabs/TabPanel'
import { editAccount } from '@/Components/Pages/Accounts/actions'
import DetailFormFields from '@/Components/Pages/Accounts/DetailFormFields'
import PasswordFormFields from '@/Components/Pages/Accounts/PasswordFormFields'
import { usePage } from '@/Components/Pages/PageProvider'
import { AccountPageProps } from '@/Pages/Accounts/Account'

const a11yProps = (index: number) => {
  return {
    id: `account-tab-${index}`,
    'aria-controls': `account-tabpanel-${index}`
  }
}

export const Page: React.FC<Pick<AccountPageProps, 'timeZoneOptions'>> = ({
  timeZoneOptions
}) => {
  const { currentUser: user, setCurrentUser } = usePage()
  const csrfToken = useCsrfToken()
  const [tabIndex, setTabIndex] = useState<number>(0)
  const [saved, setSaved] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const methods = useForm<User>({
    defaultValues: user,
    mode: 'onBlur'
  })

  const onSubmit = useCallback(
    async (data: User) => {
      setLoading(true)
      let updatedUser: Partial<User> = {
        roleIds: user.roles.ids?.map((r: string | number) => String(r))
      }
      if (tabIndex === 0) {
        updatedUser = { ...data, ...updatedUser }
      } else {
        updatedUser = { ...updatedUser, password: data.password as string }
      }
      const result = await editAccount({
        userId: user.id,
        updatedUser,
        csrfToken
      })

      if (result.error) {
        setError(result.error || 'Failed to update account')
        setLoading(false)
        return
      }
      setCurrentUser({
        ...data,
        name: [data.firstName, data.lastName].join(' ')
      })
      setLoading(false)
      setSaved(true)
      if (result.loginRequired) {
        window.location.href = '/'
      }
    },
    [csrfToken, user, setCurrentUser]
  )

  useEffect(() => {
    setSaved(false)
    setError(null)
  }, [tabIndex])

  const handleTabChange = (_e: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue)
  }

  const handleInputChange = useCallback(() => {
    setSaved(false)
    setError(null)
  }, [])

  return (
    <FormCard>
      <Stack gap={4}>
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Details" {...a11yProps(0)} />
          <Tab label="Password" {...a11yProps(1)} />
        </Tabs>
        {saved && (
          <Alert
            severity="success"
            onClose={() => setSaved(false)}
            sx={{ alignItems: 'center' }}
          >
            <AlertTitle m={0}>
              {tabIndex === 0
                ? 'Your account has been updated'
                : 'Your password has been updated'}
            </AlertTitle>
          </Alert>
        )}
        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{ alignItems: 'center' }}
          >
            <AlertTitle>Error</AlertTitle>
            <Typography>{error}</Typography>
          </Alert>
        )}
        <FormProvider {...methods}>
          <Stack
            id="account-form"
            component="form"
            onSubmit={methods.handleSubmit(onSubmit)}
            gap={4}
            onChange={handleInputChange}
          >
            <Box>
              <TabPanel index={0} value={tabIndex} title="details">
                <DetailFormFields timeZoneOptions={timeZoneOptions} />
              </TabPanel>
              <TabPanel index={1} value={tabIndex} title="password">
                <PasswordFormFields />
              </TabPanel>
            </Box>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              loading={loading}
            >
              Save account {tabIndex === 0 ? 'details' : 'password'}
            </Button>
          </Stack>
        </FormProvider>
      </Stack>
    </FormCard>
  )
}

export default Page
